import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig"; // Annahme: Firebase Functions Instanz wird hier exportiert
import analyticsService from '../services/analyticsService';
import { writePendingCheckout } from '../utils/checkoutTracking';
import type { NudgePromotionId } from '../config/nudgePromotions';

/** Optionale Revenue-Metadaten zum Checkout (aus dem Stripe-Preis abgeleitet). */
interface CheckoutMeta {
  value?: number;    // Euro (unitAmount/100), 2-Dezimal — NICHT gerundet
  currency?: string; // ISO-4217 UPPERCASE
  /**
   * Free→Paid-Nudge-Attribution (M-N4, architecture-nudge.md §7.5): die auslösende
   * Promotion-ID aus `?ref=` auf der Abo-Seite. Wird additiv als `begin_checkout`-
   * custom-param `nudge_ref` durchgereicht — nur wenn gesetzt (fail-closed vom
   * Aufrufer via `parseNudgeRef`), sonst bleibt das #214-Event unverändert.
   */
  nudgeRef?: NudgePromotionId;
}

/**
 * Grund, warum `createCheckoutSession` keine (normale) Checkout-URL liefert
 * (architecture-past-due-reactivation.md §7.1, aus `details.reason` des
 * Callable-Fehlers). `"unknown"` deckt fehlende/unerwartete Werte ab —
 * Aufrufer dürfen sich NIE auf einen rohen String verlassen.
 */
export type CheckoutFailureReason =
  | "active_subscription"
  | "payment_pending"
  | "checkout_in_progress"
  | "stripe_unavailable"
  | "unknown";

const KNOWN_CHECKOUT_FAILURE_REASONS: readonly CheckoutFailureReason[] = [
  "active_subscription",
  "payment_pending",
  "checkout_in_progress",
  "stripe_unavailable",
];

/**
 * Fehler beim Start eines Checkouts — erhält (anders als das vormalige
 * `new Error(...)`) den Callable-`code` und den fachlichen `reason` aus
 * `details.reason` (§7.1). `message` bleibt wörtlich wie IST
 * ("Checkout konnte nicht gestartet werden: …"), damit bestehende
 * `err.message`-Konsumenten unverändert funktionieren.
 */
export class CheckoutStartError extends Error {
  /** Callable-Fehlercode ohne "functions/"-Präfix, oder `null` wenn nicht ermittelbar. */
  readonly code: string | null;
  readonly reason: CheckoutFailureReason;

  constructor(message: string, code: string | null, reason: CheckoutFailureReason) {
    super(message);
    this.name = "CheckoutStartError";
    this.code = code;
    this.reason = reason;
    // Object.setPrototypeOf nötig, weil TS beim Kompilieren auf ES5 die
    // Error-Subclass-Prototypenkette sonst kappt (`instanceof` würde sonst
    // in älteren Ziel-Konfigurationen fehlschlagen).
    Object.setPrototypeOf(this, CheckoutStartError.prototype);
  }
}

/** Liest `details.reason` aus einem Firebase-Callable-Fehler, fail-closed auf `"unknown"`. */
function extractCheckoutFailureReason(err: unknown): CheckoutFailureReason {
  const details = (err as { details?: unknown } | null | undefined)?.details;
  const reason =
    details && typeof details === "object" ? (details as { reason?: unknown }).reason : undefined;
  return typeof reason === "string" &&
    (KNOWN_CHECKOUT_FAILURE_REASONS as readonly string[]).includes(reason)
    ? (reason as CheckoutFailureReason)
    : "unknown";
}

/** Liest `code` aus einem Firebase-Callable-Fehler und entfernt das "functions/"-Präfix. */
function extractCheckoutErrorCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code.replace(/^functions\//, "") : null;
}

/**
 * Antwort von `createCheckoutSession` (architecture-past-due-reactivation.md §5.4).
 * Der Bestandszweig liefert weiterhin `{url}` OHNE `kind` (Bestandsassertions
 * `toEqual({url})` bleiben grün); während der Zahlungsausfall-Kulanzfrist
 * liefert der Server stattdessen `{kind:"pay_open_invoice", url}` — beides ist
 * ein Redirect auf eine Stripe-Seite, nur der angezeigte Hinweis unterscheidet sich.
 */
type CreateCheckoutSessionResult = { url: string } | { kind: "pay_open_invoice"; url: string };

/**
 * Ruft die Cloud Function auf, um eine Stripe Checkout Session für ein Abonnement zu erstellen.
 * @param priceId Die ID des Stripe-Preises, der abonniert werden soll.
 * @param meta Optionale Revenue-Metadaten (value/currency) für `begin_checkout`/`purchase`
 *   sowie die optionale Nudge-Attribution (`nudgeRef` → `begin_checkout.nudge_ref`, M-N4).
 * @returns Eine Promise, die zur Checkout- bzw. Bezahl-URL aufgelöst wird (Signatur
 *   unverändert — der Aufrufer unterscheidet die beiden Fälle nicht, der Server tut es).
 * @throws {CheckoutStartError} wenn der Checkout nicht erstellt werden kann; trägt
 *   `code`/`reason` für ein gezieltes Fehler-Mapping in der UI (§7.2).
 */
export const startSubscriptionCheckout = async (priceId: string, meta?: CheckoutMeta): Promise<string> => {
  if (!priceId) {
    throw new Error("Price ID darf nicht leer sein.");
  }

  // Analytics: `begin_checkout` beim INTENT feuern (vor dem Callable-Call) —
  // robust gegen Callable-Latenz/Abbruch. Ersetzt den vormaligen
  // trackSubscriptionAction-Aufruf im Erfolgspfad (§6.3). Die tote
  // priceId.includes('premium')-Heuristik entfällt ersatzlos.
  // `nudge_ref` (M-N4) additiv: der Service nimmt ihn nur in die custom_parameters
  // auf, wenn gesetzt — fehlt er, feuert `begin_checkout` byte-identisch zum #214-Bestand.
  analyticsService.trackBeginCheckout({
    price_id: priceId,
    value: meta?.value,
    currency: meta?.currency,
    nudge_ref: meta?.nudgeRef,
  });

  const createCheckout = httpsCallable<
    { priceId: string; successUrl: string; cancelUrl: string },
    CreateCheckoutSessionResult
  >(functions, "createCheckoutSession");

  // Redirect-Ziele nach Erfolg/Abbruch. Die Routen leben unter `/dashboard/*`
  // (fixt Bruch B3: die vormaligen `/abo-erfolg`/`/abo-abgebrochen` liefen in
  // den Catch-all → Live-404). `{CHECKOUT_SESSION_ID}` ist ein dokumentierter
  // Stripe-Platzhalter, den Stripe beim Redirect ersetzt (Server reicht die URL
  // verbatim durch) → trägt die Purchase-Dedup-`transaction_id` an die Success-Seite.
  const successUrl = `${window.location.origin}/dashboard/abo-erfolg?session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${window.location.origin}/dashboard/abo-abgebrochen`;

  try {
    console.log(`Requesting checkout session for price: ${priceId}`);
    const result = await createCheckout({
      priceId: priceId,
      successUrl: successUrl,
      cancelUrl: cancelUrl,
    });

    if (!result.data || !result.data.url) {
      console.error("Checkout Session URL fehlt in der Antwort:", result.data);
      throw new Error("Die Checkout-URL konnte nicht abgerufen werden.");
    }

    // Zahlungsausfall-Kulanzfrist (§5.4): der Server liefert statt eines
    // normalen Checkouts einen Link auf die Stripe-Bezahlseite der offenen
    // Rechnung. Beides ist ein Redirect — KEIN writePendingCheckout (keine
    // neue Kauf-Anbahnung), ein eigenes Analytics-Event ohne Beträge/IDs.
    if ("kind" in result.data && result.data.kind === "pay_open_invoice") {
      console.log("Open-invoice payment URL received:", result.data.url);
      analyticsService.trackEvent({
        event_name: "subscription_invoice_payment_started",
        event_category: "subscription",
      });
      return result.data.url;
    }

    console.log("Checkout session URL received:", result.data.url);

    // Pending-Record NACH URL-Erhalt (der Caller redirected direkt danach) —
    // trägt value/currency über die Stripe-Navigation zur Success-Seite, wo
    // Stripe nur die session_id zurückgibt. Bei Callable-Fehler wird das nie
    // erreicht (kein verwaister Record).
    writePendingCheckout({
      priceId: priceId,
      value: meta?.value,
      currency: meta?.currency,
    });

    return result.data.url;
  } catch (error: any) {
    console.error("Fehler beim Erstellen der Checkout Session:", error);

    // Analytics: Fehler bei Checkout-Erstellung tracken
    analyticsService.trackEvent({
      event_name: 'subscription_checkout_error',
      event_category: 'subscription',
      event_label: error.message || 'unknown_error',
      custom_parameters: {
        price_id: priceId,
        error_message: error.message || 'unknown'
      }
    });

    // Versuche, eine verständlichere Fehlermeldung zu geben
    const message = error.message || "Ein unbekannter Fehler ist aufgetreten.";
    // Wirf CheckoutStartError weiter (statt eines rohen Error), damit die
    // aufrufende Komponente `code`/`reason` für ein gezieltes Mapping nutzen
    // kann, ohne die SDK-Fehlerform selbst zu kennen (§7.1/§7.2).
    throw new CheckoutStartError(
      `Checkout konnte nicht gestartet werden: ${message}`,
      extractCheckoutErrorCode(error),
      extractCheckoutFailureReason(error),
    );
  }
};

/**
 * Ruft die Cloud Function auf, um eine Stripe Billing Portal Session zu erstellen.
 * @returns Eine Promise, die zur Portal-URL aufgelöst wird.
 * @throws Wirft einen Fehler, wenn das Portal nicht gestartet werden kann.
 */
export const redirectToCustomerPortal = async (): Promise<string> => {
  const createPortal = httpsCallable<{ returnUrl: string }, { url: string }>(
    functions,
    "createPortalSession"
  );

  // URL, zu der der Nutzer nach Verlassen des Portals zurückkehrt: die
  // Konto-Seite lebt unter `/dashboard/konto` (dieselbe /dashboard-Präfix-Regel
  // wie die Checkout-Redirects — sonst Live-404 im Catch-all).
  const returnUrl = `${window.location.origin}/dashboard/konto`;

  try {
    console.log("Requesting customer portal session...");
    const result = await createPortal({
      returnUrl: returnUrl,
    });

    if (!result.data.url) {
      console.error("Customer Portal URL fehlt in der Antwort:", result.data);
      throw new Error("Die Portal-URL konnte nicht abgerufen werden.");
    }
    console.log("Customer portal URL received:", result.data.url);
    return result.data.url;
  } catch (error: any) {
    console.error("Fehler beim Starten des Kundenportals:", error);
    const message = error.message || "Ein unbekannter Fehler ist aufgetreten.";
    // Wirf den Fehler weiter, damit die aufrufende Komponente reagieren kann
    throw new Error(`Kundenportal konnte nicht geöffnet werden: ${message}`);
  }
};
